<script setup lang="ts">
import { formatCurrency, dateRelative } from "@/libs/filters";
import { useCordovaStore } from "@/stores/cordova";

const props = defineProps(["ad"]);
const cordovaStore = useCordovaStore();
const openAd = function (link: string) {
  if (cordovaStore.isLoaded) {
    cordovaStore.openInExternalBrowser(link);
  } else {
    window.open(link, "_blank");
  }
};
</script>

<template>
  <li>
    <a
      :href="ad.link"
      target="_blank"
      rel="nofollow noopener noreferrer"
      @click.prevent="openAd(ad.link)"
    >
      <div class="content">
        <div class="header-background"></div>
        <img
          v-if="ad.imagesUrl.length"
          class="image"
          :src="ad.imagesUrl[0]"
          alt=""
        />
        <div class="body">
          <div class="is-professional-seal">
            <img
              v-if="ad.isProfessional"
              src="@/assets/images/badge_pro.svg"
              alt=""
              title="Profissional"
            />
            <img
              v-else
              src="@/assets/images/badge_personal.svg"
              alt=""
              title="Pessoal"
            />
          </div>
          <div class="title">
            <h2>
              {{ ad.brandName }} <span class="model">{{ ad.modelName }}</span>
              {{ ad.year }}
            </h2>
          </div>
          <div v-if="ad.variantName" class="variant">
            {{ ad.variantName.toUpperCase() }}
          </div>
          <div v-if="ad.price" class="price">
            {{ formatCurrency(ad.price) }}
          </div>
          <div
            v-if="ad.fipePercent"
            class="fipe-seal d-none d-sm-block d-md-none d-lg-block"
            :class="{ 'fipe-seal-up': ad.fipePrice - ad.price < 0 }"
          >
            <span class="fipe-seal-number"
              >{{ (ad.fipePercent * 100).toFixed(0) }}%</span
            >
            <br />
            da FIPE
          </div>
          <div v-if="ad.fipePrice" class="profit">
            <span v-if="ad.fipePrice - ad.price > 0" class="bold">
              <span class="d-inline d-sm-none d-md-inline d-lg-none"
                >{{ (ad.fipePercent * 100).toFixed(0) }}% da FIPE -
              </span>
              {{ formatCurrency(ad.fipePrice - ad.price) }}
              abaixo
            </span>
            <span v-else class="bold">
              ({{ formatCurrency(ad.price - ad.fipePrice) }} acima)
            </span>
          </div>
        </div>
      </div>
      <div class="footer">
        <span class="created-at"
          >{{ dateRelative(props.ad.createdAt) }} na {{ props.ad.source }}</span
        >
        <span class="location">{{ ad.city }} - {{ ad.state }}</span>
      </div>
    </a>
  </li>
</template>

<style scoped>
li {
  border-radius: 0.5rem;
  list-style: none;
}

li + li {
  margin-top: 1.5rem;
}

li a {
  display: block;
  text-decoration: none;
}

.content {
  flex-direction: row;
  position: relative;
  display: flex;
  padding: 0 1.5rem 1rem;
  border-radius: 0.5rem 0.5rem 0 0;
  min-width: 0;
  background-color: var(--dark-color);
  color: var(--white-ice);
  z-index: 0;
}

.header-background {
  position: absolute;
  left: 0;
  top: 0;
  background-color: var(--black-eigengrau);
  border-radius: 0.5rem 0.5rem 0 0;
  width: 100%;
  height: 65px;
  z-index: 1;
}

.footer {
  background-color: var(--black-eigengrau);
  border-radius: 0 0 0.5rem 0.5rem;
  padding: 4px 1.5rem;
  color: var(--white-ice);
  font-size: 12px;
  display: flex;
  justify-content: space-between;
}

.image {
  width: 175px;
  height: 175px;
  object-fit: cover;
  border-radius: 0.375rem;
  z-index: 2;
  margin: 1.5rem 1rem 0 0;
  flex-shrink: 0;
}

.body {
  flex-grow: 1;
  z-index: 2;
}

.is-professional-seal {
  position: absolute;
  top: 18px;
  right: 1.5rem;
}

.is-professional-seal img {
  height: 35px;
  width: 35px;
  display: block;
}

.title {
  margin-top: 10px;
  padding-right: calc(35px + 1rem);
  height: 48px;
  display: flex;
  align-items: flex-end;
}

.title h2 {
  color: inherit;
  font-family: inherit;
  margin: 0;
  line-height: 1.2;
  font-weight: 700;
  font-size: 20px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  max-height: 48px;
}

.title .model {
  color: var(--primary-color);
}

.variant {
  font-size: 16px;
  margin-top: 12px;
  padding-right: calc(75px + 1rem);
}

.price {
  font-size: 28px;
  color: var(--primary-color);
  font-weight: 700;
  padding-right: calc(75px + 1rem);
}

.fipe-seal {
  background-color: var(--highlight-color);
  color: var(--white-ice);
  font-size: 16px;
  border-radius: 0.25rem;
  padding: 0 10px;
  display: inline-block;
  position: absolute;
  right: 1.5rem;
  top: 75px;
  text-align: center;
}

.fipe-seal-number {
  font-size: 22px;
}

.fipe-seal-up {
  background-color: var(--black-eigengrau);
}

.profit {
  font-style: italic;
  font-weight: 300;
  padding-right: calc(75px + 1rem);
}

@media (max-width: 575.98px) {
  .image {
    width: 80px;
    height: 128px;
  }

  .fipe-seal {
    display: none;
  }

  .variant {
    padding-right: 0;
  }

  .price {
    padding-right: 0;
  }

  .profit {
    padding-right: 0;
  }
}

@media (min-width: 768px) and (max-width: 991.98px) {
  .variant {
    padding-right: 0;
  }

  .price {
    padding-right: 0;
  }

  .profit {
    padding-right: 0;
  }
}
</style>
